import { Link } from 'react-router-dom';
import { Upload, ClipboardList, Sparkles, Shield, Cpu, CheckCircle, Lock } from 'lucide-react';

export default function LandingPage() {
  return (
    <div className="bg-white">
      {/* Banner */}
      <div className="fixed w-full bg-gray-900 text-gray-300 text-center text-xs py-2 z-50 tracking-wide uppercase">
        Private Beta Opening
      </div>

      {/* Navigation */}
      <nav className="fixed w-full bg-white/90 backdrop-blur-md z-50 top-7">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <img src="/branding/filie-logo-full.svg" alt="Filie" className="h-8" />
          <div className="flex items-center space-x-6">
              <Link to="/updates" className="text-gray-500 hover:text-gray-800 transition-colors text-sm">
                Updates
              </Link>
              <Link to="/tax-reference" className="text-gray-500 hover:text-gray-800 transition-colors text-sm">
                Tax Reference
              </Link>
              <Link to="/disclosures" className="text-gray-500 hover:text-gray-800 transition-colors text-sm">
                Disclosures
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-36 pb-28 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <div className="mb-10 flex justify-center">
            <img src="/branding/filie-logo-full.svg" alt="Filie" className="h-16" />
          </div>
          <h1 className="text-5xl sm:text-6xl font-extrabold text-gray-900 mb-6 leading-[1.1] tracking-tight">
            Know your taxes.
          </h1>
          <p className="text-lg sm:text-xl text-gray-500 mb-10 max-w-xl mx-auto leading-relaxed font-light">
            Upload your documents, see every number explained, and verify what matters before you file.
          </p>
          <div className="flex justify-center">
            <button
              onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center justify-center text-gray-600 hover:text-gray-900 px-8 py-3.5 rounded-full font-medium transition-all duration-200 border border-gray-200 hover:border-gray-300 bg-white"
            >
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-5xl mx-auto px-6"><div className="border-t border-gray-100" /></div>

      {/* Features Section */}
      <section id="how-it-works" className="py-24 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest text-center mb-3">How it works</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-4">
            Three steps to clarity.
          </h2>
          <p className="text-base text-gray-500 text-center mb-16 max-w-md mx-auto">
            Filie is built to help you understand your return, not just rush you through it.
          </p>

          <div className="grid md:grid-cols-3 gap-10">
            {[
              { icon: Upload, title: 'Upload Documents', desc: 'Add your W-2s, 1099s, and tax forms. AI reads the documents so you do less manual entry.', num: '01' },
              { icon: ClipboardList, title: 'Review What Filie Found', desc: 'Answer a short plain-English questionnaire and confirm the values Filie extracted from your documents.', num: '02' },
              { icon: Sparkles, title: 'Understand Your Return', desc: 'See what came from documents, what you entered, and what Filie calculated before you download your forms.', num: '03' },
            ].map((f) => (
              <div key={f.num} className="relative">
                <span className="text-6xl font-black text-gray-100 absolute -top-4 -left-2">{f.num}</span>
                <div className="relative pt-8">
                  <f.icon className="w-5 h-5 text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest text-center mb-3">Why people trust Filie</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-4">
            Built for trust.
          </h2>
          <p className="text-base text-gray-500 text-center mb-16 max-w-lg mx-auto">
            Filie is designed to be transparent about where numbers come from, what still needs review, and how sensitive data is handled.
          </p>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { icon: Lock, title: 'Sensitive IDs stay out of AI processing', desc: 'SSNs and EINs are extracted locally and redacted before any document is sent to an AI provider.' },
              { icon: Cpu, title: 'AI reads. Filie computes.', desc: 'AI extracts fields from your forms. Tax math like AGI, brackets, credits, and refund is handled by Filie&apos;s deterministic engine.' },
              { icon: CheckCircle, title: 'You can verify every extracted value', desc: 'Before you rely on a result, you can review what Filie read from each document and correct anything that looks off.' },
              { icon: Shield, title: 'Documents are stored with encryption at rest', desc: 'Documents are stored with encryption at rest, traffic is protected with TLS, and sensitive identifiers are encrypted in our systems.' },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-xl p-6 border border-gray-100">
                <item.icon className="w-5 h-5 text-gray-400 mb-3" />
                <h3 className="text-sm font-semibold text-gray-900 mb-1.5">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-400 text-center mt-10">
            Deterministic tax math, not chatbot arithmetic
          </p>
        </div>
      </section>

      {/* Coming Soon CTA */}
      <section className="py-24 px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Private beta is opening to a small group of early users.
          </h2>
          <p className="text-gray-500 mb-6">
            If you want a tax product that explains the return instead of asking for blind trust, request access and follow the build.
          </p>
          <div className="flex flex-col items-center justify-center gap-4">
            <a
              href="mailto:info@filie.ai?subject=Filie%20Private%20Beta"
              className="inline-flex items-center justify-center rounded-full bg-gray-900 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-gray-800"
            >
              Request Beta Access
            </a>
            <div className="flex items-center justify-center space-x-4">
            <a href="https://x.com/filieai" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-800 transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
            <a href="https://linkedin.com/company/filie" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-800 transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-10 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center space-x-3 mb-6 md:mb-0">
            <img src="/branding/filie-icon.svg" alt="Filie" className="w-6 h-6" />
            <span className="font-semibold text-gray-900 text-sm">Filie</span>
            <span className="text-gray-300 text-sm">&middot;</span>
            <span className="text-gray-400 text-sm">Know your taxes.</span>
          </div>
          <div className="flex items-center space-x-6">
            <Link to="/updates" className="text-sm text-gray-400 hover:text-gray-600 transition-colors">Updates</Link>
              <Link to="/privacy" className="text-sm text-gray-400 hover:text-gray-600 transition-colors">Privacy</Link>
            <Link to="/security" className="text-sm text-gray-400 hover:text-gray-600 transition-colors">Security</Link>
            <Link to="/terms" className="text-sm text-gray-400 hover:text-gray-600 transition-colors">Terms</Link>
            <Link to="/disclosures" className="text-sm text-gray-400 hover:text-gray-600 transition-colors">Disclosures</Link>
            <a href="https://x.com/filieai" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-gray-500 transition-colors" aria-label="Twitter/X">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
            <a href="https://linkedin.com/company/filie" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-gray-500 transition-colors" aria-label="LinkedIn">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </a>
            <a href="https://github.com/filieai" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-gray-500 transition-colors" aria-label="GitHub">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>
            </a>
          </div>
        </div>
        <div className="max-w-5xl mx-auto mt-6">
          <p className="text-xs text-gray-300 text-center md:text-left">© 2026 Filie. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
